import StepConfig from 'libs/fields-manager/src/shared/stores/models/StepConfig';
import { RequestObjectExtraFieldsDataType } from 'libs/fields-manager/src/shared/types/zodSchemaTypes';

function parseJsonSafe(raw: unknown): any {
  if (raw == null || raw === '') {
    return null;
  }
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

/**
 * IB `form_field_config` may be either:
 * - Full multi-step: `{ registration_form_details: { step_details: [StepConfig, ...] } }`
 * - Minimal: `{ fields_data: [...] }` (no `registration_form_details`)
 */
function getFirstStepFromFormFieldConfig(formFieldConfig: any): any | null {
  if (!formFieldConfig || typeof formFieldConfig !== 'object') {
    return null;
  }

  const fromRegistration =
    formFieldConfig.registration_form_details?.step_details?.[0];
  if (fromRegistration) {
    return fromRegistration;
  }

  const fromTopLevelSteps = formFieldConfig.step_details?.[0];
  if (fromTopLevelSteps) {
    return fromTopLevelSteps;
  }

  const fieldsData = formFieldConfig.fields_data;
  if (Array.isArray(fieldsData) && fieldsData.length > 0) {
    return {
      step_id:
        formFieldConfig.initial_step_id ?? 'APPLICATION_SECTION_FORM_STEP',
      step_type: 'DEFAULT',
      fields_data: fieldsData
    };
  }

  return null;
}

function flattenRequestObjectFields(fieldsData: any[] | undefined): any[] {
  if (!Array.isArray(fieldsData)) {
    return [];
  }

  return fieldsData.flatMap((fieldConfig: any) => {
    if (
      fieldConfig?.type === 'formFieldGroup' &&
      Array.isArray(fieldConfig?.form_fields)
    ) {
      return flattenRequestObjectFields(fieldConfig.form_fields);
    }

    if (!fieldConfig?.field) {
      return [];
    }

    return [fieldConfig];
  });
}

function inferRequestObjectKeysFromFieldsData(
  fieldsData: any[] | undefined
): RequestObjectExtraFieldsDataType {
  return flattenRequestObjectFields(fieldsData).map((f: any) => ({
    field: f.field,
    tags: Array.isArray(f.tags) ? f.tags : ['NON_PII'],
    field_name_in_request_object: f.field
  }));
}

class ApplicationSectionFormModel {
  formDetails: any;
  formValidationSchema: any;
  formsRequestObjectKeys: RequestObjectExtraFieldsDataType;
  onSubmitRedirectionUrl?: string;
  formConfig: any;

  currentDateTime: Date = new Date();

  constructor(sectionFormConfig: any) {
    if (!sectionFormConfig || typeof sectionFormConfig !== 'object') {
      this.formDetails = null;
      this.formsRequestObjectKeys = [];
      this.formValidationSchema = { schema: {} };
      return;
    }

    this.currentDateTime =
      sectionFormConfig?.current_datetime &&
      new Date(sectionFormConfig?.current_datetime);

    const formFieldConfig = parseJsonSafe(sectionFormConfig?.form_field_config);
    const formConfig = parseJsonSafe(sectionFormConfig?.form_config);
    let formSectionConfigModel = null;
    let formsRequestObjectKeys: RequestObjectExtraFieldsDataType = [];
    const formValidationSchema = parseJsonSafe(
      sectionFormConfig?.form_validation_config
    );

    if (formFieldConfig) {
      const firstStep = getFirstStepFromFormFieldConfig(formFieldConfig);
      if (firstStep) {
        formSectionConfigModel = new StepConfig(firstStep, null);
      }
      const extra = formFieldConfig.request_object_extra_fields_data;
      formsRequestObjectKeys = Array.isArray(extra)
        ? extra
        : inferRequestObjectKeysFromFieldsData(formFieldConfig.fields_data);
    }

    if (formConfig) {
      this.formConfig = formConfig;
    }
    this.formDetails = formSectionConfigModel;
    this.formsRequestObjectKeys = formsRequestObjectKeys;
    if (sectionFormConfig?.on_submit_redirect_url) {
      this.onSubmitRedirectionUrl = sectionFormConfig.on_submit_redirect_url;
    }
    if (formValidationSchema) {
      this.formValidationSchema = formValidationSchema;
    } else {
      this.formValidationSchema = {
        schema: {}
      };
    }
  }
}

export default ApplicationSectionFormModel;
